import { Paper, Title, SimpleGrid, Text, Stack, Center, Loader, ThemeIcon, Group, Badge } from '@mantine/core';
import { IconUsers, IconClick, IconArrowBounce, IconEye, IconDeviceMobile, IconDeviceDesktop, IconDeviceTablet } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import apiClient from '@/api/client';
import { useRefreshInterval } from './RefreshContext';

interface VisitSummary {
  nb_uniq_visitors: number;
  nb_visits: number;
  nb_actions: number;
  bounce_rate: string;
  avg_time_on_site: number;
}

interface DeviceStats {
  desktop?: number;
  smartphone?: number;
  tablet?: number;
  [key: string]: number | undefined;
}

interface AnalyticsData {
  today: VisitSummary;
  yesterday: VisitSummary;
  last7days: VisitSummary;
  liveVisitors: number;
  devices: {
    today: DeviceStats;
    last7days: DeviceStats;
  };
}

function useAnalytics() {
  const interval = useRefreshInterval();
  return useQuery({
    queryKey: ['cockpit', 'analytics'],
    queryFn: async (): Promise<AnalyticsData> => {
      const res = await apiClient.get('/analytics');
      return res.data;
    },
    refetchInterval: interval,
  });
}

export function MatomoPanel() {
  const { data, isLoading, error } = useAnalytics();

  if (error) {
    return (
      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="md">Website-Traffic (Matomo)</Title>
        <Text c="red">Fehler beim Laden</Text>
      </Paper>
    );
  }

  if (isLoading || !data) {
    return (
      <Paper shadow="xs" p="md" withBorder>
        <Title order={4} mb="md">Website-Traffic (Matomo)</Title>
        <Center><Loader size="sm" /></Center>
      </Paper>
    );
  }

  return (
    <Paper shadow="xs" p="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={4}>Website-Traffic (Matomo)</Title>
        {data.liveVisitors > 0 && (
          <Badge color="green" variant="filled" size="sm">{data.liveVisitors} live</Badge>
        )}
      </Group>
      <SimpleGrid cols={2}>
        <Stack align="center" gap={4}>
          <ThemeIcon size="lg" variant="light" color="blue">
            <IconUsers size={20} />
          </ThemeIcon>
          <Text size="xl" fw={700}>{data.today.nb_uniq_visitors}</Text>
          <Text size="xs" c="dimmed">Besucher heute</Text>
          <Text size="xs" c="dimmed">({data.yesterday.nb_uniq_visitors} gestern)</Text>
        </Stack>
        <Stack align="center" gap={4}>
          <ThemeIcon size="lg" variant="light" color="teal">
            <IconClick size={20} />
          </ThemeIcon>
          <Text size="xl" fw={700}>{data.today.nb_actions}</Text>
          <Text size="xs" c="dimmed">Aktionen heute</Text>
          <Text size="xs" c="dimmed">({data.yesterday.nb_actions} gestern)</Text>
        </Stack>
        <Stack align="center" gap={4}>
          <ThemeIcon size="lg" variant="light" color="orange">
            <IconArrowBounce size={20} />
          </ThemeIcon>
          <Text size="xl" fw={700}>{data.today.bounce_rate}</Text>
          <Text size="xs" c="dimmed">Bounce Rate heute</Text>
        </Stack>
        <Stack align="center" gap={4}>
          <ThemeIcon size="lg" variant="light" color="grape">
            <IconEye size={20} />
          </ThemeIcon>
          <Text size="xl" fw={700}>{data.last7days.nb_visits}</Text>
          <Text size="xs" c="dimmed">Besuche (7 Tage)</Text>
        </Stack>
      </SimpleGrid>

      {data.devices && (
        <>
          <Title order={5} mt="md" mb="xs">Geräte heute</Title>
          <SimpleGrid cols={3}>
            <Stack align="center" gap={4}>
              <ThemeIcon size="lg" variant="light" color="indigo">
                <IconDeviceDesktop size={20} />
              </ThemeIcon>
              <Text size="lg" fw={700}>{data.devices.today.desktop ?? 0}</Text>
              <Text size="xs" c="dimmed">Desktop</Text>
            </Stack>
            <Stack align="center" gap={4}>
              <ThemeIcon size="lg" variant="light" color="cyan">
                <IconDeviceMobile size={20} />
              </ThemeIcon>
              <Text size="lg" fw={700}>{data.devices.today.smartphone ?? 0}</Text>
              <Text size="xs" c="dimmed">Mobile</Text>
            </Stack>
            <Stack align="center" gap={4}>
              <ThemeIcon size="lg" variant="light" color="violet">
                <IconDeviceTablet size={20} />
              </ThemeIcon>
              <Text size="lg" fw={700}>{data.devices.today.tablet ?? 0}</Text>
              <Text size="xs" c="dimmed">Tablet</Text>
            </Stack>
          </SimpleGrid>
          <Text size="xs" c="dimmed" ta="center" mt={4}>
            7 Tage: {data.devices.last7days.desktop ?? 0} Desktop · {data.devices.last7days.smartphone ?? 0} Mobile · {data.devices.last7days.tablet ?? 0} Tablet
          </Text>
        </>
      )}
    </Paper>
  );
}
